// How task groups and tasks read on the admin pages: status tones, source and requirement
// labels, counts by status and the list filters. Pure, so the pages only lay things out.
import type { Graph } from "../catalog/types";
import { messages } from "../i18n";
import { runTone, type Tone } from "../ui/tones";
import type { GroupStatus, RequirementState, Source, Task, TaskGroup, TaskStatus, TaskStep } from "./model";
import { TASK_STATUSES } from "./model";

export const GROUP_TONE: Record<GroupStatus, Tone> = { active: "green", paused: "orange", archived: "gray" };

export const TASK_TONE: Record<TaskStatus, Tone> = {
  queued: "gray",
  running: "blue",
  waiting_for_human: "orange",
  succeeded: "green",
  failed: "red",
  cancelled: "gray",
};

/** Ready states are green, the two that stop a task from starting red, the rest informational. */
export const REQUIREMENT_TONE: Record<RequirementState, Tone> = {
  bound: "green",
  platform: "primary",
  unbound: "gray",
  missing: "red",
  broken: "red",
  not_enforced: "gray",
  recorded: "gray",
};

/** "acme/payments · label agent", "project = PAY", or nothing for a manual group. */
export function sourceDetail(group: TaskGroup): string {
  const c = group.source_config;
  const text = (k: string) => (typeof c[k] === "string" ? (c[k] as string) : undefined);
  switch (group.source) {
    case "manual":
      return "";
    case "github": {
      const labels = Array.isArray(c.labels) ? c.labels.filter((l) => typeof l === "string") : [];
      return [text("repo"), labels.length > 0 ? messages().tasks.sourceLabels(labels.join(", ")) : undefined].filter(Boolean).join(" · ");
    }
    case "jira":
      return text("jql") ?? "";
    case "asana":
      return [text("project"), text("section")].filter(Boolean).join(" / ");
  }
}

/** A poll cadence (`poll_minutes`, 1–1440): "Every 15 min", "Every hour", "Every 2 h 30 min". */
export function cadenceLabel(minutes: number): string {
  const c = messages().tasks.cadence;
  if (minutes < 60) return c.minutes(minutes);
  if (minutes === 60) return c.hour;
  if (minutes === 1440) return c.day;
  return c.hours(Math.floor(minutes / 60), minutes % 60);
}

export type StatusCounts = Record<TaskStatus, number>;

/**
 * The status a task reads as (S141): one whose step's agent waits on questions, or that is held
 * for its environment's VPN (S153), is still `running` on the wire, but for its owner it waits
 * on them like an approval does.
 */
export function shownStatus(task: Pick<Task, "status" | "waiting_on">): TaskStatus {
  const w = task.waiting_on;
  if (!w) return task.status;
  // D87: a held subtask is queued behind its parent; a parent waits on its owner only while
  // one of its subtasks does.
  if (w.kind === "parent") return task.status;
  if (w.kind === "subtasks") return w.waiting > 0 ? "waiting_for_human" : task.status;
  return "waiting_for_human";
}

export function countByStatus(tasks: Task[]): StatusCounts {
  const counts = Object.fromEntries(TASK_STATUSES.map((s) => [s, 0])) as StatusCounts;
  for (const t of tasks) counts[shownStatus(t)]++;
  return counts;
}

/** A node's name in the task's graph, or its id. */
export function nodeName(graph: Graph | undefined, id: string | null | undefined): string {
  if (!id) return "—";
  const node = graph?.nodes.find((n) => n.id === id);
  return node?.name || id;
}

export interface GroupFilter {
  status: GroupStatus | "all";
  source: Source | "";
  taskType: string;
  owner: string;
}

export function filterGroups(groups: TaskGroup[], f: GroupFilter): TaskGroup[] {
  return groups.filter(
    (g) =>
      (f.status === "all" || g.status === f.status) &&
      (f.source === "" || g.source === f.source) &&
      (f.taskType === "" || g.task_type_id === f.taskType) &&
      (f.owner === "" || g.owner_user_id === f.owner),
  );
}

/** A step's outcome as a pill: an open step is running (prompt) or waiting (approval). */
export function stepOutcome(step: Pick<TaskStep, "kind" | "outcome">): { label: string; tone: Tone } {
  if (step.outcome === null || step.outcome === undefined) {
    return step.kind === "approval" ? { label: "waiting", tone: "orange" } : { label: "running", tone: "blue" };
  }
  return { label: step.outcome, tone: step.outcome === "answered" ? "green" : runTone(step.outcome) };
}

/**
 * Cancel any unfinished task, retry a failed or cancelled one, start any finished one over
 * (D86) or move it back to a step it ran (D89); only the owner may.
 */
export function taskActions(status: TaskStatus): { cancel: boolean; retry: boolean; startOver: boolean; moveBack: boolean } {
  const retry = status === "failed" || status === "cancelled";
  const finished = retry || status === "succeeded";
  return { cancel: ["queued", "running", "waiting_for_human"].includes(status), retry, startOver: finished, moveBack: finished };
}
